import { Link as LinkComponent } from "@/components";
import { Download, ExternalLink, Shape } from "@/icons";
import { IconInfoType } from "@/sections/FaqSections";
import { GetValues, Link, LinkTypeEnum } from "@anoki/fse-common/dist";
import React, { useId } from "react";
import { ArrowLink } from "../ArrowLink";
import { Button } from "../Button";
import { SvgIcon } from "../SvgIcon";
import { saveAs } from "file-saver";
import { ColorKeys } from "@/theme/colors";
import "./ActionBanner.scss";

export enum ActionBannerVariantEnum {
  DEFAULT = "default",
  OUTLINE = "outline",
  SIMPLE = "simple",
}

export type ActionBannerVariantTypes = GetValues<
  typeof ActionBannerVariantEnum
>;

type WithLinkType<T> = Link & { linkType?: T };

interface ActionBannerProps {
  title: string;
  description: string | React.ReactNode;
  variant?: ActionBannerVariantTypes;
  link?: WithLinkType<LinkTypeEnum>;
  withIcon?: boolean;
  icon?: IconInfoType;
  firstAction?: WithLinkType<LinkTypeEnum>;
  secondAction?: WithLinkType<LinkTypeEnum>;
  maxWidth?: string;
  minHeight?: string;
  linkPositionFixed?: boolean;
}

const DownloadButton: React.FC<{
  linkData: WithLinkType<LinkTypeEnum>;
  onDownload: (linkData: WithLinkType<LinkTypeEnum>, title: string) => void;
  iconColor?: ColorKeys;
  textColor?: ColorKeys;
}> = ({
  linkData,
  onDownload,
  iconColor = "primary",
  textColor = "primary",
}) => {
  return (
    <Button
      className="bg-transparent download-button"
      onClick={() => onDownload(linkData, linkData.title)}
      aria-label={`Scarica ${linkData.title}`}
    >
      <span
        style={{
          color: `var(--${textColor})`,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Download color={iconColor} aria-hidden="true" />
        {linkData.title}
      </span>
    </Button>
  );
};

export const ActionBanner: React.FC<ActionBannerProps> = ({
  title,
  description,
  variant = "default",
  withIcon = false,
  icon,
  link,
  firstAction,
  secondAction,
  maxWidth = "642px",
  minHeight,
  linkPositionFixed = false,
}) => {
  const titleId = useId();

  const handleDownload = async (
    linkData: WithLinkType<LinkTypeEnum>,
    linkTitle: string,
  ) => {
    try {
      const response = await fetch(linkData.link);
      const blob = await response.blob();
      saveAs(blob, linkTitle);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const renderLinkAction = (
    linkData: WithLinkType<LinkTypeEnum> | undefined,
  ) => {
    if (!linkData || !linkData.title) return null;

    if (linkData.linkType === LinkTypeEnum.DOWNLOAD) {
      return <DownloadButton linkData={linkData} onDownload={handleDownload} />;
    }

    if (linkData.isExternal) {
      return (
        <LinkComponent
          title={linkData.title}
          src={linkData.link}
          icon={ExternalLink}
          external={true}
        />
      );
    }

    return (
      <ArrowLink asLink="a" href={linkData.link} isExternal={false}>
        {linkData.title}
      </ArrowLink>
    );
  };

  const renderActionButton = (
    actionData: WithLinkType<LinkTypeEnum> | undefined,
    buttonVariant: "secondary" | "primary",
  ) => {
    if (!actionData || !actionData.title) return null;

    if (actionData.linkType === LinkTypeEnum.DOWNLOAD) {
      return (
        <DownloadButton linkData={actionData} onDownload={handleDownload} />
      );
    }

    if (actionData.isExternal) {
      return (
        <LinkComponent
          title={actionData.title}
          src={actionData.link}
          icon={ExternalLink}
          decoration="ui-footer-logo-link"
          transform="none"
          external={true}
        />
      );
    }

    return (
      <Button
        variant={buttonVariant}
        className={buttonVariant === "secondary" ? "left-button" : ""}
        asLink="a"
        href={actionData.link}
        border="primary"
        isExternal={false}
      >
        {actionData.title}
      </Button>
    );
  };

  return (
    <section
      className={`action-banner action-banner-${variant}`}
      aria-labelledby={titleId}
      style={{ maxWidth, minHeight, width: "100%", height: "100%" }}
    >
      <header className="banner-head">
        <h2 id={titleId} className="banner-title fs-3 md-fs-2">
          {title}
        </h2>
      </header>
      <div className="banner-body" style={linkPositionFixed ? { flex: 1 } : undefined}>
        {withIcon && (
          <div className="banner-icon" aria-hidden="true">
            {icon ? (
              <SvgIcon icon={icon} className="banner-icon__svg" />
            ) : (
              <Shape />
            )}
          </div>
        )}
        <div className="banner-description text-slate-28">{description}</div>
      </div>
      <div className="banner-actions">
        {renderLinkAction(link)}
        {renderActionButton(firstAction, "primary")}
        {renderActionButton(secondAction, "secondary")}
      </div>
    </section>
  );
};
